<template>
    <fullscreen-drawer :visible="visible" height="auto" title="Approval History" :closable="false">
        <template #header-button>
            <i class="fal fa-lg fa-chevron-left" @click="handleClose" />
        </template>
        <g-base-box v-if="deliverable && deliverable.is_complete" class="g-mb-5">
            <g-row>
                <g-column :span="1">
                    <g-icon name="exclamation-circle" color="red-5" size="small"/>
                </g-column>
                <g-column :span="22" class="g-ml-1">
                    <g-typography tag="span">
                        Approval activity can be viewed for 30 days after a deliverable has been completed.
                        After this time, it will be permanently deleted.
                    </g-typography>
                </g-column>
            </g-row>
        </g-base-box>
        <div class="item-history-row" v-if="deliverable && deliverable.is_complete">
            <g-typography color="grey-6" tag="p">{{ contact.first_name + ' ' + contact.last_name }} on {{ getMonth(deliverable.updated_at) }} at {{ getTime(deliverable.updated_at) }}</g-typography>
            <base-box v-if="deliveredUrl" border-radius="large" class="delivered-url-box g-p-4">
                <div slot="content">
                    <a :href="deliveredUrl" target="_blank">{{ deliveredUrl }}</a>
                </div>
            </base-box>
            <base-box
                border-radius="large"
                padding="1em"
                class="item-box"
            >
                <div slot="content">
                    <g-icon name="check-circle" />
                    {{ CONTENT_APPROVAL_ERROR_MESSAGES[CONTENT_APPROVAL_STATUS_COMPLETED] }}
                </div>
            </base-box>
        </div>
        <div v-if="deliverable">
            <div v-for="item in deliverable.approval_content_activity.slice().reverse()" :key="item.id" class="item-history-row">
                <g-typography tag="p">
                    {{ item.doer_name }} on {{ getMonth(item.updated_at) }} at
                    {{ getTime(item.updated_at) }}
                </g-typography>
                <div v-if="item.content && item.content.description && item.type === CONTENT_SUBMISSION">
                    <div
                        v-for="file in fileList(item.content.files)"
                        :key="file.id"
                        class="margin-bottom-normal margin-top-normal"
                    >
                        <activation-file v-if="file" :file="file" :deletable="false" />
                    </div>
                </div>
                <g-typography v-if="item.content.description" class="margin-bottom-normal" color="grey-6" tag="p">
                    {{ item.content.description }}
                </g-typography>
                <g-typography v-if="item.action_reason" class="margin-bottom-normal" tag="p">
                    {{ item.action_reason }}
                </g-typography>
                <base-box border-radius="large" padding="1em" class="item-box">
                    <div slot="content" class="content-activity-status-box">
                        <g-icon
                            color="orange-4"
                            :name="
                                item.type === DELIVERABLE_CONTENT_ACTIVITY_STATUS_REVIEW
                                    ? 'clock'
                                    : item.type === DELIVERABLE_CONTENT_ACTIVITY_STATUS_APPROVED ||
                                      item.type === DELIVERABLE_CONTENT_ACTIVITY_STATUS_COMPLETED
                                    ? 'check-circle '
                                    : 'exclamation-circle'
                            "
                        />
                        <g-typography tag="p">
                            {{ DELIVERABLE_CONTENT_ACTIVITY_ERROR_MESSAGES[item.type ? item.type : ''] }}
                        </g-typography>
                    </div>
                </base-box>
            </div>
        </div>
        <div class="approval-history-footer">
            <powered-by-grin />
        </div>
    </fullscreen-drawer>
</template>

<script>
    import FullscreenDrawer from '../../../../library/FullscreenDrawer.vue';
    import { getMediaLibraryFilePaths } from '../../../../../helpers/activations/activation-files';
    import {
        CONTENT_APPROVAL_ERROR_MESSAGES,
        CONTENT_APPROVAL_STATUS_REJECTED,
        CONTENT_APPROVAL_FILE_TYPE,
        CONTENT_APPROVAL_STATUS_APPROVED,
        CONTENT_APPROVAL_STATUS_NONE,
        CONTENT_APPROVAL_STATUS_REVIEW,
        CONTENT_APPROVAL_STATUS_COLORS,
        CONTENT_APPROVAL_STATUS_COMPLETED,
        CONTENT_APPROVAL_STATUS_LABELS
    } from '../../../../../constants/content-approval';
    import {
        DELIVERABLE_CONTENT_ACTIVITY_STATUS_LABELS,
        DELIVERABLE_CONTENT_ACTIVITY_ERROR_MESSAGES,
        DELIVERABLE_CONTENT_ACTIVITY_STATUS_COMPLETED,
        DELIVERABLE_CONTENT_ACTIVITY_STATUS_NONE,
        DELIVERABLE_CONTENT_ACTIVITY_STATUS_REVIEW,
        DELIVERABLE_CONTENT_ACTIVITY_STATUS_APPROVED,
        DELIVERABLE_CONTENT_ACTIVITY_STATUS_REJECTED
    } from '../../../../../constants/deliverable-content-activity';
    import ActivationFile from '../../../../library/ActivationFile.vue';
    import { useContactStore } from '../../../../../../stores';
    import moment from 'moment';
    import { computed } from 'vue-demi';

    export default {
        name: 'ActivationDeliverableContentApprovalHistoryDrawer',

        components: {
            FullscreenDrawer,
            ActivationFile
        },

        props: {
            deliverable: {
                type: Object,
                default: null
            },
            visible: {
                type: Boolean,
                default: false
            }
        },

        setup(props, context) {
            const CONTENT_SUBMISSION = 'content_submission';
            const contact = useContactStore().data;

            const getMonth = (jsDate) => {
                return moment(jsDate).format('MMM D, YYYY');
            };

            const getTime = (jsDate) => {
                return moment(jsDate).format('h:mm a');
            };

            const handleClose = () => {
                context.emit('close');
            };

            const fileList = (files) => {
                return getMediaLibraryFilePaths(files, CONTENT_APPROVAL_FILE_TYPE);
            };

            const deliveredUrl = computed(() => {
                return (props.deliverable?.content || []).length > 0 ? props.deliverable?.content[0]?.content_link : '';
            });

            return {
                CONTENT_SUBMISSION,
                CONTENT_APPROVAL_ERROR_MESSAGES,
                CONTENT_APPROVAL_STATUS_NONE,
                CONTENT_APPROVAL_STATUS_APPROVED,
                CONTENT_APPROVAL_STATUS_REVIEW,
                CONTENT_APPROVAL_STATUS_COLORS,
                CONTENT_APPROVAL_STATUS_REJECTED,
                CONTENT_APPROVAL_STATUS_COMPLETED,
                CONTENT_APPROVAL_STATUS_LABELS,
                DELIVERABLE_CONTENT_ACTIVITY_STATUS_COMPLETED,
                DELIVERABLE_CONTENT_ACTIVITY_STATUS_NONE,
                DELIVERABLE_CONTENT_ACTIVITY_STATUS_REVIEW,
                DELIVERABLE_CONTENT_ACTIVITY_STATUS_APPROVED,
                DELIVERABLE_CONTENT_ACTIVITY_STATUS_REJECTED,
                DELIVERABLE_CONTENT_ACTIVITY_STATUS_LABELS,
                DELIVERABLE_CONTENT_ACTIVITY_ERROR_MESSAGES,
                getMonth,
                getTime,
                handleClose,
                fileList,
                contact,
                deliveredUrl
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-top-normal {
        margin-top: $g-spacing-5;
    }

    .font-grey {
        color: $g-color-grey-6;
    }

    ::v-deep .FullscreenDrawer__content {
        max-width: $g-screen-size-xs !important;
    }

    .fa-clock {
        color: $g-color-primary-6;
    }

    .fa-check-circle {
        color: $g-color-green-7;
    }

    .item-history-row {
        margin-bottom: 2em;
        padding-bottom: 2em;
        border-bottom: solid $g-color-grey-4 1px;

        &:last-child(n) {
            border-bottom: none;
        }
    }

    .item-box {
        padding: $g-spacing-5;
        margin-top: $g-spacing-5;
        margin-bottom: $g-spacing-3;
        background: $g-color-grey-2;
    }
    .approval-history-footer {
        display: flex;
        justify-content: center;
    }
    .content-activity-status-box {
        display: flex;

        p {
            margin-left: 0.5em;
            color: $g-color-grey-6;
        }
    }
    .delivered-url-box {
        margin-top: $g-spacing-5;
    }
</style>
